import { makeAutoObservable } from 'mobx';
import ErrorBoundaryStore from '../error-boundary-store/error-boundary-store';
import {
  CuePoint,
  CuePointMetadata,
  Presentation,
  Segment,
  CuePointSource,
  CaptionPropertyList,
  Track,
  TrackSettings,
} from './presentation-config-aggregate/presentation-config-aggregate-types/presentation-config-aggregate-types';
import PresentationConfigAggregate from './presentation-config-aggregate/presentation-config-aggregate';
import { TGraphQLSubscriptions } from '../../api/graphql-api-client/graphql-api-client';
import { CommandsInput, PublishSegmentsActionType } from '../../graphql/API';
import AuthStore, { IDecodedJwtToken } from '../auth-store/auth-store';
import { getPodiumInfoStore } from '../podium-info-store/podium-info-store-instance';
import { getErrorBoundaryStore } from '../error-boundary-store/error-boundary-store-instanse';
import PodiumInfoStore from '../podium-info-store/podium-info-store';
import { ITERATE_NUMBER } from '../../constans/polls-constants';
import CuePointEntity from './presentation-config-aggregate/cue-point-entity/cue-point-entity';
import { getPermissionsStore } from '../permissions-store/permissions-store-instance';
import PermissionsStore from '../permissions-store/permissions-store';
import { PresenterTabsOnMobile } from '../../constans/presenter-enums';
import { getAuthStore } from '../auth-store/auth-store-intance';
import SegmentEntity from './presentation-config-aggregate/segment-entity/segment-entity';
import { buildPresenterSlideItemsFromSegmentsArray } from '../../utils/helpers/presenter-slides-helpers';
import {
  changeSlideCommandSubscriptionApi,
  changeSlideMutationsApi,
  getLivePresentationInfoApi,
  getPresentationSlidesApi,
  ICommandObject,
  ILivePresentationInfo,
  ISlideCommandObject,
  SlidesCommand,
  updateSlideDeckSubscriptionApi,
  publishSegmentsMutation,
  ISegmentOptions
} from '../../api/dao/presenter-slide-dao';
import SlideManagementEntity from './presentation-config-aggregate/slide-managment-entity/slide-management-entity';
import { SLIDE_NOT_EXIST_MESSAGE } from '../../constans/presenter-constansts';
import { PresenterActivePointer } from './presentation-config-aggregate/presenter-active-pointer/presenter-active-pointer';
import MixpanelService from '../../utils/services/mixpanel-service';
import { getMixpanelService } from '../../utils/services/mixpanel-service-instance';
import { MIXPANEL_EVENT_IDENTIFIERS, MIXPANEL_EVENT_TYPES } from '../../constans/mixpanel-contants';
import { v4 as uuidv4 } from 'uuid';
import {
  defaultSettingsProperties,
  defaultCuePointProperties,
  defaultSourceProperties,
  defaultMetadataProperties
} from '../../constans/presenter-slide-constants';
import { IErrorObject, IGraphQLErrorResponse } from '../../utils/services/error-parsing-service';
import { LiveStreamStatus } from '../../constans/presenter-enums';

class PresenterSlideManagementStore {
  public isLoading: boolean;
  public lastUserNameModifyTheSlides: string | null;
  private isOpenSlideAdvancingPopUp: boolean;
  private closeNotificationTimeOut: ReturnType<typeof setTimeout>;
  private presenterInfo: IDecodedJwtToken | null;
  private livePresentationInfo: ILivePresentationInfo | null;
  private restoredCuePoint: CuePointEntity | null;
  private mobileActiveNavigationTab: PresenterTabsOnMobile;
  private lastUserIdModifyTheSlides: string | number | null;
  private isSlidesFullscreenOpen: boolean = false;

  constructor (
    private readonly kvxAggregate: PresentationConfigAggregate = new PresentationConfigAggregate(),
    private readonly slideManagementEntity: SlideManagementEntity = new SlideManagementEntity(),
    private readonly podiumInfoStore: PodiumInfoStore = getPodiumInfoStore(),
    private readonly errorBoundary: ErrorBoundaryStore = getErrorBoundaryStore(),
    private readonly permissionsStore: PermissionsStore = getPermissionsStore(),
    private readonly authStore: AuthStore = getAuthStore(),
    private readonly mixpanelService: MixpanelService = getMixpanelService(),
    private readonly additionalIndex: number = ITERATE_NUMBER
  ) {
    makeAutoObservable(this);
    this.errorBoundary = getErrorBoundaryStore();
    this.mobileActiveNavigationTab = PresenterTabsOnMobile.QUESTIONS;
    this.isLoading = false;
    this.isOpenSlideAdvancingPopUp = false;
    this.lastUserIdModifyTheSlides = getAuthStore().getPresenterUserId();
    this.lastUserNameModifyTheSlides = null;
    this.livePresentationInfo = null;
    this.restoredCuePoint = null;
  }

  public readonly getSlideManagementEntity = (): SlideManagementEntity => {
    return this.slideManagementEntity;
  };

  get activeMobileNavigationTab (): PresenterTabsOnMobile {
    return this.mobileActiveNavigationTab;
  }

  get isSlidesManagementAvailable (): boolean {
    return this.permissionsStore.isPodiumSlidesEnabled() && !!this.slideManagementEntity.getNumberOfSlides();
  }

  get getIsSlidesFullscreenOpen (): boolean {
    return this.isSlidesFullscreenOpen;
  }

  public returnLivePresentationInfo = (): ILivePresentationInfo | null => {
    return this.livePresentationInfo;
  };

  public toggleFullscreenModeAction = (): void => {
    this.isSlidesFullscreenOpen = !this.isSlidesFullscreenOpen;
  };

  public setPresenterInfo = (presenterInfo: IDecodedJwtToken | null): void => {
    this.presenterInfo = presenterInfo;
  };

  public setLastUserNameModifyTheSlides = (userName: string | null): void => {
    this.lastUserNameModifyTheSlides = userName || 'Unknown user';
  };

  public isShowSlideAdvancingPopUp = (): boolean => {
    return !!(this.isOpenSlideAdvancingPopUp && this.slideManagementEntity.getNumberOfSlides());
  };

  public forceCloseSlideNotification = (): void => {
    clearTimeout(this.closeNotificationTimeOut);
    this.isOpenSlideAdvancingPopUp = false;
  };

  public setActiveMobileNavigationTab = (tab: PresenterTabsOnMobile): void => {
    this.mobileActiveNavigationTab = tab;
  };

  public isPrevArrowDisabled = (): boolean => {
    return this.slideManagementEntity.isSelectPrevSlideDisabled() || this.isLoading;
  };

  public isNextArrowDisabled = (): boolean => {
    return this.slideManagementEntity.isSelectNextSlideDisabled() || this.isLoading;
  };

  public goToNextSlideAction = (): void => {
    const { isSelectNextSlideDisabled, getActivePointer } = this.slideManagementEntity;
    this.mixpanelService.track(MIXPANEL_EVENT_IDENTIFIERS.PRESENTERS_TAB, MIXPANEL_EVENT_TYPES.RIGHT_CLICK);
    if (isSelectNextSlideDisabled()) return;
    this.changeSlideCommandCall(getActivePointer().getCurrentCuePointIndex() + this.additionalIndex);
  };

  public goToPrevSlideAction = (): void => {
    const { isSelectPrevSlideDisabled, getActivePointer } = this.slideManagementEntity;
    this.mixpanelService.track(MIXPANEL_EVENT_IDENTIFIERS.PRESENTERS_TAB, MIXPANEL_EVENT_TYPES.LEFT_CLICK);
    if (isSelectPrevSlideDisabled()) return;
    this.changeSlideCommandCall(getActivePointer().getCurrentCuePointIndex() - this.additionalIndex);
  };

  public selectSlideInVerticalList = (index: number): void => {
    const isSameSlide: boolean = this.slideManagementEntity.getActivePointer().getCurrentCuePointIndex() === index;
    if (isSameSlide) return;
    this.changeSlideCommandCall(index);
    this.mixpanelService.track(MIXPANEL_EVENT_IDENTIFIERS.PRESENTER, MIXPANEL_EVENT_TYPES.SLIDE_DECK_CHANGED);
  };

  public getPresentationSlides = async (): Promise<void> => {
    const presentationId = this.podiumInfoStore.getPresentationId() || '';
    if (!presentationId) return;
    try {
      const configs = await getPresentationSlidesApi(presentationId);
      this.kvxAggregate.init(configs);
      await this.checkCuePointInRestoredDataAndInitSlides();
    } catch (e) {
      this.errorBoundary.setErrorMessage(e as IErrorObject);
    }
  };

  public getLivePresentationInfo = async (): Promise<void> => {
    const presentationId = this.podiumInfoStore.getPresentationId() || '';
    if (!presentationId) return;
    try {
      this.livePresentationInfo = await getLivePresentationInfoApi(presentationId);
    } catch (e) {
      this.errorBoundary.setErrorMessage(e as IErrorObject);
    }
  };

  private checkCuePointInRestoredDataAndInitSlides = async (): Promise<void> => {
    if (!this.kvxAggregate.isLiveBroadcast()) return;
    await this.getLivePresentationInfo();
    this.initSlideManagementEntity();
  };

  private initSlideManagementEntity = () => {
    const isRestoredCuePoint = !!this.livePresentationInfo?.restored_data.cuepoint;
    const presenterActivePointer: PresenterActivePointer = new PresenterActivePointer(this.slideManagementEntity);
    this.slideManagementEntity.setActivePointer(presenterActivePointer);
    if (isRestoredCuePoint && !this.isLiveStatusEnded()) {
      this.restoredCuePoint = new CuePointEntity(this.livePresentationInfo?.restored_data.cuepoint);
      this.slideManagementEntity.initSlides(this.kvxAggregate.getAllWorkingSequence(), this.restoredCuePoint.getCuePointId());
    }
    else {
      this.slideManagementEntity.initSlides(this.kvxAggregate.getAllWorkingSequence());
    }
  };

  private changeSlideCommandCall = (currentSlideIndex: number): void => {
    if (this.isLiveStatusEnded()) {
      this.slideManagementEntity.selectSlide(currentSlideIndex);
      return;
    }
    this.changeSlideCommand(currentSlideIndex);
  };

  private addSlidesCallback = (segments: SegmentEntity[], insertIndex: number | null): void => {
    const presenterSlidesItemsFromSegment = buildPresenterSlideItemsFromSegmentsArray(segments);
    !this.slideManagementEntity.getNumberOfSlides() && this.setActiveMobileNavigationTab(PresenterTabsOnMobile.CLICKER);
    this.slideManagementEntity.addSlides(presenterSlidesItemsFromSegment, insertIndex);
  };

  private removeSlideCallback = (segments: SegmentEntity[]): void => {
    const [presenterSlideFromSegment] = buildPresenterSlideItemsFromSegmentsArray(segments);
    this.slideManagementEntity.removeSlide(presenterSlideFromSegment);
  };

  private updateSlideCallback = (segments: SegmentEntity[]): void => {
    const [presenterSlideFromSegment] = buildPresenterSlideItemsFromSegmentsArray(segments);
    const isSlideExist = !!this.slideManagementEntity.getSlideByCuePointId(presenterSlideFromSegment.getCuePointId());
    if (isSlideExist) {
      this.slideManagementEntity.updateSlideMetadata(presenterSlideFromSegment);
    }
    else this.throwErrorOnCallback();
  };

  private purgeSlideCallback = (): void => {
    this.slideManagementEntity.purgeSlides();
  };

  private reorderSlideCallback = (segments: SegmentEntity[], indexToPut: number | null): void => {
    const [presenterSlideFromSegment] = buildPresenterSlideItemsFromSegmentsArray(segments);
    const isSlideExist = !!this.slideManagementEntity.getSlideByCuePointId(presenterSlideFromSegment.getCuePointId());
    if (isSlideExist) {
      this.slideManagementEntity.reorderSlide(presenterSlideFromSegment, indexToPut);
    } else {
      this.throwErrorOnCallback();
    }
  };

  private updateSlideSubscriptionReducer = (payload: { onPublishSegments: ISlideCommandObject }): void => {
    const { action, segments, atIndex } = payload.onPublishSegments;
    const parsedSegment: Segment[] = JSON.parse(segments) || [];
    const segmentsEntityArray = parsedSegment.map((segment: Segment) => new SegmentEntity(segment));

    switch (action) {
      case SlidesCommand.ADD:
        return this.addSlidesCallback(segmentsEntityArray, atIndex);
      case SlidesCommand.REMOVE:
        return this.removeSlideCallback(segmentsEntityArray);
      case SlidesCommand.REORDER:
        return this.reorderSlideCallback(segmentsEntityArray, atIndex);
      case SlidesCommand.UPDATE:
        return this.updateSlideCallback(segmentsEntityArray);
      case SlidesCommand.PURGE:
        return this.purgeSlideCallback();
      default:
        return;
    }
  };

  private showNotificationWithCloseActionByTimeOut = (): void => {
    const milliseconds = 5000;
    this.isOpenSlideAdvancingPopUp = true;
    this.closeNotificationTimeOut = setTimeout(() => {
      this.isOpenSlideAdvancingPopUp = false;
    }, milliseconds);
  };

  public slideDeckUpdateSubscription = (): TGraphQLSubscriptions => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    return updateSlideDeckSubscriptionApi(
      presentationId,
      this.updateSlideSubscriptionReducer,
      () => this.slideDeckUpdateSubscription());
  };

  public changeSlideCommandSubscription = (): TGraphQLSubscriptions => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    return changeSlideCommandSubscriptionApi(
      presentationId,
      this.onChangeSlideCallback,
      () => this.changeSlideCommandSubscription());
  };

  public publishSegments = async (options: ISegmentOptions): Promise<void> => {
    const presentationId = this.podiumInfoStore.getPresentationId();

    if (!presentationId) return;

    try {
      await this.getPresentationSlides();
      const configs = await getPresentationSlidesApi(presentationId);
      const segments = this.generateSegment(configs, options);
      const nextSlideIndex = this.slideManagementEntity.getActivePointer()?.getCurrentCuePointIndex() + ITERATE_NUMBER;
      const atIndex = this.slideManagementEntity.getNumberOfSlides() === 0 ? 0 : nextSlideIndex;
      const publishSegmentsBody = {
        segments,
        atIndex,
        action: PublishSegmentsActionType.ADD,
      };

      await publishSegmentsMutation(publishSegmentsBody, presentationId);
    } catch (error) {
      this.errorBoundary.setGraphQLErrorMessage(error as IGraphQLErrorResponse);
    }
  };

  private generateSegment = (configs: Presentation, options: ISegmentOptions): string => {
    const { assetId, largeThumbnail, smallThumbnail } = options;
    const segment: Segment = configs.project.timeline.sequences.sequence[0].segments.segment[0];
    const trackProperties: Track = segment?.tracks?.track[0];
    const trackSettings: TrackSettings = trackProperties.settings;
    const cuePointId = uuidv4();

    const metadata: CuePointMetadata = {
      ...defaultMetadataProperties,
      smallThumbnail,
      largeThumbnail,
    };

    const properties: CaptionPropertyList = {
      property: [
        ...defaultSourceProperties,
        {
          name: 'cuePointId',
          value: `${cuePointId}`
        },
        {
          name: 'assetId',
          value: `${assetId}`
        }
      ]
    };

    const source: CuePointSource = {
      url: largeThumbnail,
      image: largeThumbnail,
      properties
    };

    const cuePoint: CuePoint[] = [{
      ...defaultCuePointProperties,
      settings: defaultSettingsProperties,
      metadata,
      source,
    }];

    const generatedSegment: Segment[] = [{
      ...segment,
      tracks: {
        track: [{
          cuePoints: {
            cuePoint
          },
          settings: trackSettings
        }]
      }
    }];

    return JSON.stringify(generatedSegment);
  };

  private onChangeSlideCallback = (data: { onChangeSlides: ICommandObject }): void => {
    const { onChangeSlides: commandObject } = data;

    if (this.canSelectNewIndexOnSubscription(commandObject.currentSlideIndex)) {
      this.slideManagementEntity.selectSlide(commandObject.currentSlideIndex);
      if (commandObject.concurrencyUserId === this.authStore.getPresenterUserId()) {
        this.lastUserIdModifyTheSlides = commandObject.concurrencyUserId;
        return;
      } else if (commandObject.concurrencyUserId !== this.lastUserIdModifyTheSlides?.toString()) {
        this.showNotificationWithCloseActionByTimeOut();
        this.lastUserIdModifyTheSlides = commandObject.concurrencyUserId;
        this.setLastUserNameModifyTheSlides(commandObject.userName);
      }
    }
  };

  private changeSlideCommand = async (currentSlide: number): Promise<void> => {
    const livePresentationInfo = this.livePresentationInfo;
    const presentationId = this.podiumInfoStore.getPresentationId() || '';

    if (!livePresentationInfo?.id || !this.presenterInfo || !presentationId) return;

    const commandDTO = this.buildCommandDTO(currentSlide, presentationId, livePresentationInfo.id);

    try {
      this.isLoading = true;
      const result = await changeSlideMutationsApi(commandDTO);
      this.lastUserIdModifyTheSlides = result.data?.commandsMutation?.concurrencyUserId || null;
    }
    catch (e) {
      this.errorBoundary.setGraphQLErrorMessage(e as IGraphQLErrorResponse);
    } finally {
      this.isLoading = false;
    }
  };

  private buildCommandDTO = (slideIndex: number, presentationId: string, livePresentationInfoId: number): CommandsInput => {
    const { userId, knovioUserId, userName } = this.presenterInfo!;
    const cuePoint = this.slideManagementEntity.getSlideByIndex(slideIndex);
    const content = JSON.stringify(cuePoint.getCuePoint().serialize());

    return {
      concurrencyUserId: userId || knovioUserId?.toString(),
      presentationId: presentationId,
      content,
      livePresentationId: livePresentationInfoId.toString(),
      currentSlideIndex: slideIndex,
      userName
    };
  };

  private canSelectNewIndexOnSubscription = (slideIndex: number): boolean => {
    return !!this.slideManagementEntity.getSlideByIndex(slideIndex);
  };

  private isLiveStatusEnded = (): boolean => {
    return this.livePresentationInfo?.live_status === LiveStreamStatus.ENDED;
  };

  private throwErrorOnCallback = (): void => {
    return this.errorBoundary.setErrorMessageExplicitly(SLIDE_NOT_EXIST_MESSAGE);
  };

}

export default PresenterSlideManagementStore;
